<template>
  <div>
    <b-sidebar
      id="sidebar-discount-coupon"
      sidebar-class="sidebar-lg"
      :visible="newDiscountCouponSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateNewDiscountCouponSidebar({ visible: val })"
      @hidden="clear"
      @shown="onShow"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4> {{newDiscountCouponSidebar.id ? 'Editar' : 'Novo'}} cupom de desconto</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <template>
          <!-- Form -->
          <b-row class="justify-content-md-center mt-2" v-if="loading">
            <b-spinner big />
          </b-row>
          <b-form
            v-else
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
            class="p-1"
          >
            <!-- Code -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group
                  label="Código do cupom"
                  label-for="discount-coupon-code"
                >
                  <b-form-input
                    id="discount-coupon-code"
                    v-model="code"
                    :class="{ 'is-invalid': v$.code.$error }"
                    required
                    @input="code = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.code.required.$invalid">
                      Você deve informar um código para o cupom
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Description -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group
                  label="Descrição"
                  label-for="discount-coupon-description"
                >
                  <b-form-input
                    id="discount-coupon-description"
                    v-model="description"
                    :class="{ 'is-invalid': v$.description.$error }"
                    required
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.description.required.$invalid">
                      Você deve informar uma descrição para o cupom
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Percentage -->
            <b-row>
              <b-col class="mt-1">
                <percentage-input
                  id="import-payment-percentage"
                  label="Percentual de desconto"
                  v-model="percentage"
                  :thereIsError="v$.percentage.$error && v$.percentage.$invalid"
                  errorMessage="Você deve fornecer uma porcentagem válida entre 1 e 100"
                  :disabled="quantityUsed > 0"
                />
                <small 
                  class="text-muted" 
                  v-if="quantityUsed > 0" 
                >
                  Não é possível alterar o percentual de cupons já utilizados
                </small>
                <b-alert
                  v-if="couponPaymentAlert"
                  :variant="couponPaymentAlert.variant"
                  show
                  class="mt-1 mb-0"
                >
                  {{ couponPaymentAlert.message }}
                </b-alert>

              </b-col>
            </b-row>

            <!-- Quantity -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group
                  label="Quantidade"
                  label-for="discount-coupon-quantity"
                >
                  <b-form-input
                    id="discount-coupon-quantity"
                    v-model="quantity"
                    type="number"
                    :class="{ 'is-invalid': v$.quantity.$error }"                  
                    required
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.quantity.required.$invalid">
                      Você deve informar a quantidade disponível
                    </span>
                    <span
                      v-if="
                        v$.quantity.minValue.$invalid
                      "
                    >
                      O valor deve ser maior que 0.
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Is Active -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group
                  label-for="discount-coupon-is-active"
                >
                  <b-form-checkbox
                    id="discount-coupon-is-active"
                    v-model="isActive"
                    switch 
                    inline
                  >
                    Ativo
                  </b-form-checkbox>
                  <div class="invalid-feedback">
                    <span v-if="v$.isActive.required.$invalid">
                      Você deve informar se o cupom está ativo
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <b-row>
              <b-col class="mt-1">
                <div class="d-flex mt-2">
                  <b-button
                    :disabled="saving || hasBlockingPaymentRule"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="mr-2"
                    type="submit"
                  >
                    {{ saving ? "Salvando..." : "Salvar" }}
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from 'lodash'
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BRow, BCol, BSpinner, BAlert
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useVuelidate from "@vuelidate/core";
import { required, minValue, maxValue } from "@vuelidate/validators";
import { useToast } from 'vue-toastification/composition'
import { getVueSelectErrorClass } from "@/helpers/validators";
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '../store/types'
import * as sharedTypes from '@/modules/shared/store/types'
import PercentageInput from '@/modules/shared/components/PercentageInput';
import { percentageToFloat, floatToPercentage } from '@/helpers/converters'


export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BSpinner,
    BAlert,
    vSelect,
    PercentageInput
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      code: undefined,
      description: undefined,
      percentage: undefined,
      quantity: undefined,
      isActive: true,
      quantityUsed: 0,
      appPaymentRules: undefined,
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      newDiscountCouponSidebar: types.NEW_DISCOUNT_COUPON_SIDEBAR
    }),
    percentageValue() {
      return percentageToFloat(this.percentage)
    },
    discountedAppPlans() {
      const plans = this.appPaymentRules?.app_plans || []

      return plans.map(plan => {
        const amount = Number(plan.amount) || 0
        const finalAmount = Math.round((amount * (1 - (this.percentageValue / 100))) * 100) / 100

        return {
          ...plan,
          finalAmount,
        }
      })
    },
    couponPaymentAlert() {
      if (!this.percentageValue) {
        return null
      }

      if (this.percentageValue >= 100) {
        return {
          variant: 'info',
          message: 'Este cupom concederá isenção total. A assinatura será ativada sem cobrança no Asaas.',
        }
      }

      const minimumAmount = Number(this.appPaymentRules?.minimum_credit_card_amount) || 5
      const blockedPlan = this.discountedAppPlans.find(plan => (
        Number(plan.amount) > 0 && plan.finalAmount < minimumAmount
      ))

      if (!blockedPlan) {
        return null
      }

      const planName = blockedPlan.table_header || blockedPlan.name

      return {
        variant: 'danger',
        message: `Este desconto deixa o plano ${planName} em ${this.formatCurrency(blockedPlan.finalAmount)}, abaixo de ${this.formatCurrency(minimumAmount)}, mínimo aceito para pagamento por cartão. Use 100% para isenção total ou reduza o desconto.`,
      }
    },
    hasBlockingPaymentRule() {
      return this.couponPaymentAlert?.variant === 'danger'
    },
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate()};
  },
  validations() {
    return {
      code: { required },
      description: { required },
      percentage: { 
        required,
        minValue: value => percentageToFloat(value) > 1, 
        maxValue: value => percentageToFloat(value) <= 100
      },
      quantity: { required, minValue: minValue(1) },
      isActive: { required },
      
    }
  },
  methods: {
    ...mapMutations({
      mutateNewDiscountCouponSidebar: types.MUTATE_NEW_DISCOUNT_COUPON_SIDEBAR
    }),
    ...mapActions({
      saveDiscountCoupon: types.SAVE_DISCOUNT_COUPON,
      getDiscountCoupon: types.GET_DISCOUNT_COUPON,
      getAppPaymentRules: types.GET_APP_PAYMENT_RULES,
    }),
    onShow(){
      this.resetFormFields()
      this.loadAppPaymentRules()
      if(this.newDiscountCouponSidebar.id){
        this.loading = true
        this.getDiscountCoupon(this.newDiscountCouponSidebar.id)
          .then(response => {
            this.code = response.data.code
            this.description = response.data.description
            this.percentage = floatToPercentage(response.data.percentage)
            this.quantity = response.data.quantity
            this.isActive =  response.data.is_active == 1 ? true : false;
            this.quantityUsed = response.data.quantity_used
          })
          .catch(err => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!',
                text: 'Ocorreu um erro ao carregar o cupom de desconto. Entre em contato com o setor de TI.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
            this.mutateNewDiscountCouponSidebar({ visible: false })
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      if (this.hasBlockingPaymentRule) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: this.couponPaymentAlert.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      this.saving = true
      const {
        code,
        description,
        percentage,
        quantity,
        isActive
      } = this
      this.saveDiscountCoupon({
        id: this.newDiscountCouponSidebar.id,
        code: code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
        description,
        percentage: percentageToFloat(percentage),
        quantity,
        is_active: isActive 
      })
        .then(response => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: `O cupom ${code} foi salvo com sucesso!`,
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.newDiscountCouponSidebar.saveAction()
          this.mutateNewDiscountCouponSidebar({ visible: false })
        })
        .catch(err => {
          if(err.response.status == 400){
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!',
                text: 'Ocorreu um erro ao salvar o cupom. ' + err.response.data.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }else{
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!',
                text: 'Ocorreu um erro ao salvar o cupom. Entre em contato com o setor de TI.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .finally(() => {
          this.saving = false;
        });
      
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    loadAppPaymentRules() {
      this.getAppPaymentRules()
        .then(response => {
          this.appPaymentRules = response.data
        })
        .catch(() => {})
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    resetFormFields() {
      this.code = undefined
      this.description = undefined
      this.percentage = undefined
      this.quantity = undefined
      this.isActive = true
      this.quantityUsed = 0
      this.appPaymentRules = undefined
      this.loading = false
      this.saving = false
      this.v$.$reset()
    },
    clear(){
      this.resetFormFields()
      this.mutateNewDiscountCouponSidebar({ id: undefined })
    }
  }
}
</script>
